import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { Button, Card } from "../ui";

function AllegatiGallery({ richiestaId, allegati }) {
  const { getAccessToken } = useAuth();
  const [downloadingId, setDownloadingId] = useState(null);
  const [previews, setPreviews] = useState({});
  const [lightbox, setLightbox] = useState(null);
  const previewUrlsRef = useRef({});

  useEffect(() => {
    if (!allegati?.length) return;
    const imageAllegati = allegati.filter((a) =>
      a.contentType?.startsWith("image/"),
    );
    if (!imageAllegati.length) return;

    let cancelled = false;
    (async () => {
      const token = await getAccessToken();
      for (const a of imageAllegati) {
        if (cancelled) break;
        if (previewUrlsRef.current[a.id]) continue;
        try {
          const res = await fetch(
            `/api/richieste-intervento/${richiestaId}/allegati/${a.id}/download`,
            { headers: token ? { Authorization: `Bearer ${token}` } : {} },
          );
          if (!res.ok || cancelled) continue;
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          previewUrlsRef.current[a.id] = url;
          if (!cancelled) setPreviews((prev) => ({ ...prev, [a.id]: url }));
        } catch {
          /* ignora errori preview singoli */
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [allegati, richiestaId, getAccessToken]);

  useEffect(() => {
    return () => {
      Object.values(previewUrlsRef.current).forEach(URL.revokeObjectURL);
    };
  }, []);

  const downloadAllegato = async (fileId, fileName) => {
    setDownloadingId(fileId);
    try {
      const token = await getAccessToken();
      const res = await fetch(
        `/api/richieste-intervento/${richiestaId}/allegati/${fileId}/download`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (!res.ok) throw new Error(`Errore download: ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setDownloadingId(null);
    }
  };

  if (!allegati?.length) return null;

  return (
    <Card className="mb-4">
      <h2 className="font-semibold text-gray-900 mb-3">
        Allegati ({allegati.length})
      </h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {allegati.map((a) => {
          const isImage = a.contentType?.startsWith("image/");
          const previewUrl = previews[a.id];
          return (
            <div
              key={a.id}
              className="relative rounded-lg border border-gray-200 overflow-hidden bg-gray-50 flex flex-col"
            >
              {isImage ? (
                <button
                  className="w-full aspect-square overflow-hidden bg-gray-100 focus:outline-none"
                  onClick={() =>
                    previewUrl &&
                    setLightbox({ id: a.id, url: previewUrl, name: a.name })
                  }
                  title="Clicca per ingrandire"
                >
                  {previewUrl ? (
                    <img
                      src={previewUrl}
                      alt={a.name}
                      className="w-full h-full object-cover hover:opacity-90 transition-opacity"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-300 text-3xl">
                      🖼️
                    </div>
                  )}
                </button>
              ) : (
                <div className="w-full aspect-square bg-gray-100 flex items-center justify-center text-3xl">
                  📎
                </div>
              )}
              <div className="p-2 flex items-center justify-between gap-1">
                <span
                  className="text-xs text-gray-600 truncate flex-1"
                  title={a.name}
                >
                  {a.name}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  loading={downloadingId === a.id}
                  onClick={() => downloadAllegato(a.id, a.name)}
                  className="flex-shrink-0 text-xs px-2 py-1"
                >
                  ↓
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={() => setLightbox(null)}
        >
          <div
            className="relative max-w-3xl w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightbox.url}
              alt={lightbox.name}
              className="w-full max-h-[80vh] object-contain rounded-lg shadow-xl"
            />
            <div className="flex items-center justify-between mt-3 px-1">
              <span className="text-white text-sm truncate flex-1 mr-4">
                {lightbox.name}
              </span>
              <div className="flex gap-2 flex-shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  loading={downloadingId === lightbox.id}
                  onClick={() => downloadAllegato(lightbox.id, lightbox.name)}
                  className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                >
                  Download
                </Button>
                <button
                  onClick={() => setLightbox(null)}
                  className="text-white/80 hover:text-white text-2xl leading-none px-2"
                  aria-label="Chiudi"
                >
                  ×
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

export default AllegatiGallery;
